import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Icon from 'components/Icon';
import { textoDoErro } from 'services/erros';

/**
 * Copia `texto` para a área de transferência e troca o rótulo por `rotuloCopiado` por 2,5 s.
 *
 * Nasceu do copia-e-cola da assinatura (pages/Assinatura/PagamentoAssinatura) e serve também à
 * chave Pix do profissional na Agenda. O erro não tem fallback automático: `navigator.clipboard`
 * falha em contexto não seguro e em alguns navegadores embutidos, e aí quem chama já mostra o
 * texto na tela para ser selecionado à mão — é isso que o toast manda fazer. `codigoErro` é o
 * código do catálogo (services/erros) com a mensagem certa para aquela tela.
 *
 * `aria-live` no rótulo: sem ele, o leitor de tela não anuncia que copiou, porque o foco não sai
 * do botão.
 *
 * Sempre `type="button"`: há telas que o renderizam dentro de um <form>.
 */
export default function BotaoCopiar({
	texto,
	rotulo = 'Copiar',
	rotuloCopiado = 'Copiado!',
	codigoErro = 'GER-301',
	className = 'btn-primary w-full justify-center py-3',
	comIcone = false,
	onCopiado,
}) {
	const [copiado, setCopiado] = useState(false);
	const temporizador = useRef(null);

	useEffect(() => () => clearTimeout(temporizador.current), []);

	const copiar = async (e) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(texto);
			setCopiado(true);
			clearTimeout(temporizador.current);
			temporizador.current = setTimeout(() => setCopiado(false), 2500);
			onCopiado?.();
		} catch (erro) {
			toast.error(textoDoErro(codigoErro, erro));
		}
	};

	return (
		<button
			type="button"
			onClick={copiar}
			disabled={!texto}
			className={`${className} focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50`}
		>
			{comIcone && <Icon name={copiado ? 'check' : 'content_copy'} className="h-4 w-4" />}
			<span aria-live="polite">{copiado ? rotuloCopiado : rotulo}</span>
		</button>
	);
}
